/**
 * Helpers for SuperAdmin Folders factory methods
 */

import type { IBaseSuperAdmin } from "../../BaseSuperAdminFactory";
import type { OfficeFolder } from "le-coffre-resources/dist/Notary";
import EFolderStatus from "le-coffre-resources/dist/Customer/EFolderStatus";
import type { QueryPayload } from "../../../../types";
import type { IGetFoldersParams } from "../Folders";
import {
	buildFoldersCreatePayload,
	buildFoldersUpdatePayload,
	buildStatusUpdatePayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for folders
 */
export function createGetMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (q: IGetFoldersParams): Promise<OfficeFolder[]> => {
		const url = new URL(baseUrl);
		baseSuperAdmin.appendJsonSearchParams(url, q);
		try {
			return await baseSuperAdmin.getRequest<OfficeFolder[]>(url);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for folders
 */
export function createGetByUidMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		if (q) baseSuperAdmin.appendJsonSearchParams(url, q);
		try {
			return await baseSuperAdmin.getRequest<OfficeFolder>(url);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for folders
 */
export function createPostMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (officeFolder: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildFoldersCreatePayload(officeFolder);
			return await baseSuperAdmin.postRequest<OfficeFolder>(url, payload);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for folders
 */
export function createPutMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, body: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildFoldersUpdatePayload(body);
			return await baseSuperAdmin.putRequest<OfficeFolder>(url, payload);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for folders
 */
export function createDeleteMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const targetedFolder = await baseSuperAdmin.getRequest<OfficeFolder>(url);
		if (targetedFolder.customers) {
			return Promise.reject(`The folder ${uid} contains customers`);
		}
		try {
			return await baseSuperAdmin.deleteRequest<OfficeFolder>(url);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create archive method for folders
 */
export function createArchiveMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, body: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const payload = buildStatusUpdatePayload(body, EFolderStatus.ARCHIVED);
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseSuperAdmin.putRequest<OfficeFolder>(url, payload);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create restore method for folders
 */
export function createRestoreMethod(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string) {
	return async (uid: string, body: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const payload = buildStatusUpdatePayload(body, EFolderStatus.LIVE);
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseSuperAdmin.putRequest<OfficeFolder>(url, payload);
		} catch (err) {
			baseSuperAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}
